/*
  Usage:
  <collection-interrupter data-drawer="collection-interrupter-drawer">
    <button type="button" data-interrupter-button>Shop the edit</button>
  </collection-interrupter>
*/

const collectionInterrupter = class extends HTMLElement {
  constructor () {
    super()
    this.button = this.querySelector('[data-interrupter-button]')
    this.drawerId = this.getAttribute('data-drawer')
    this.unwrapped = false
    this.removeAttribute('hidden')
    this.initButton()
  }

  connectedCallback () {
    this.unwrapAnchor()
  }

  // Collection grids wrap each item in an anchor. A link cannot contain a
  // button, so take the anchor's place in the DOM instead of sitting inside it.
  unwrapAnchor () {
    if (this.unwrapped) { return }

    const anchor = this.parentElement
    if (!anchor || anchor.tagName !== 'A') { return }

    this.unwrapped = true
    anchor.replaceWith(this)
  }

  initButton () {
    if (!this.button) { return }

    this.button.addEventListener('click', () => {
      this.openDrawer()
    })
  }

  openDrawer () {
    if (!this.drawerId) { return }

    const drawer = document.querySelector(`popup-element[data-id="${this.drawerId}"]`)
    if (!drawer) { return }

    if (Shopify.designMode) {
      window.localStorage.setItem('activePopup', this.drawerId)
    }

    drawer.open = true
  }
}

if (typeof window.customElements.get('collection-interrupter') === 'undefined') {
  window.customElements.define('collection-interrupter', collectionInterrupter)
}

/*
  A single product row inside the interrupter drawer. Handles option
  selection, price/availability state, and adding to cart.
*/
const interrupterProduct = class extends HTMLElement {
  constructor () {
    super()
    this.variants = this.parseJson('[data-interrupter-variants]')
    this.media = this.parseJson('[data-interrupter-media]')
    this.imageEl = this.querySelector('.interrupter-product__image')
    this.optionGroups = this.querySelectorAll('[data-product-option-group]')
    this.optionRadios = this.querySelectorAll('[data-product-option][data-type="radio"]')
    this.priceEl = this.querySelector('[data-interrupter-price]')
    this.compareWrapEl = this.querySelector('[data-interrupter-compare-price-wrap]')
    this.compareEl = this.querySelector('[data-interrupter-compare-price]')
    this.submitTextEls = this.querySelectorAll('[data-interrupter-submit-text]')
    this.button = this.querySelector('[data-interrupter-add]')
    this.errorEl = this.querySelector('[data-interrupter-error]')
    this.drawer = this.closest('popup-element')
    this.currentVariant = this.variants.find(variant => variant.id === Number(this.getAttribute('data-variant-id'))) || null

    this.initOptions()
    this.initAdd()
  }

  parseJson (selector) {
    const script = this.querySelector(selector)
    if (!script) { return [] }

    try {
      return JSON.parse(script.textContent)
    } catch (error) {
      console.error(error)
      return []
    }
  }

  initOptions () {
    if (!this.optionGroups.length) { return }

    this.optionGroups.forEach(group => {
      group.addEventListener('change', () => {
        this.updateVariant()
      })
    })

    // Paint the initial unavailable/sold out states, the same way the
    // product page does on load.
    this.updateVariant()
  }

  // Mirrors the option handling in product.js so the shared product-options
  // markup behaves the same here as it does on the product page.
  getSelectedValues () {
    const values = []

    this.optionGroups.forEach(group => {
      const index = Number(group.dataset.optionIndex) - 1

      if (group.dataset.type === 'select') {
        const select = group.querySelector('[data-product-option]')
        values[index] = select ? select.value : null
        return
      }

      const checked = group.querySelector('[data-product-option]:checked')
      values[index] = checked ? checked.value : null
    })

    return values
  }

  getVariantByValues (values) {
    return this.variants.find(variant => {
      return values.every((value, index) => variant.options[index] === value)
    }) || null
  }

  updateVariant () {
    const values = this.getSelectedValues()
    this.currentVariant = this.getVariantByValues(values)

    this.updateOptionStates(values)
    this.updateImage()
    this.updatePrice()
    this.updateButton()
  }

  updateOptionStates (values) {
    this.optionRadios.forEach(radio => {
      const index = Number(radio.dataset.optionIndex) - 1
      const candidate = [...values]
      candidate[index] = radio.value

      const matchingVariant = this.getVariantByValues(candidate)
      const item = radio.closest('[data-product-option-item]')
      if (!item) { return }

      item.classList.toggle('unavailable', !matchingVariant)
      item.classList.toggle('soldout', matchingVariant?.available === false)

      const srLabel = item.querySelector('[data-swatch-ada-label]')
      if (!srLabel) { return }

      if (!matchingVariant) {
        srLabel.innerHTML = `${srLabel.dataset.swatchAdaLabel} - Unavailable`
      } else if (!matchingVariant.available) {
        srLabel.innerHTML = `${srLabel.dataset.swatchAdaLabel} - Sold Out`
      } else {
        srLabel.innerHTML = srLabel.dataset.swatchAdaLabel
      }
    })

    this.optionGroups.forEach(group => {
      const selectedTextEl = group.querySelector('[data-product-option-group-selected-text]')
      const selectedInput = group.querySelector('[data-product-option]:checked')
      if (!selectedTextEl || !selectedInput) { return }

      selectedTextEl.textContent = selectedInput.dataset.selectedText
    })
  }

  // The gallery matches media to variants through the image's variant list
  // and leaves the current image alone when nothing matches - same here.
  updateImage () {
    if (!this.imageEl || !this.media.length || !this.currentVariant) { return }

    const matchingMedia = this.media.find(item => item.variant_ids.includes(this.currentVariant.id))
    if (!matchingMedia) { return }

    this.imageEl.src = matchingMedia.src
    this.imageEl.alt = matchingMedia.alt
    this.imageEl.width = matchingMedia.width
    this.imageEl.height = matchingMedia.height
    this.imageEl.removeAttribute('srcset')
  }

  updatePrice () {
    if (this.priceEl) {
      const price = this.currentVariant ? this.currentVariant.price : ''
      const compare = this.currentVariant ? this.currentVariant.compare_at_price : ''

      this.priceEl.innerText = price
      this.priceEl.classList.toggle('product-price__price--sale', compare !== '')
    }

    if (this.compareWrapEl) {
      const compare = this.currentVariant ? this.currentVariant.compare_at_price : ''
      this.compareWrapEl.classList.toggle('hide', compare === '')

      if (this.compareEl) {
        this.compareEl.innerText = compare
      }
    }
  }

  updateButton () {
    if (!this.button) { return }

    this.clearError()
    this.setAttribute('data-variant-id', this.currentVariant ? this.currentVariant.id : '')

    let state = 'add'
    if (!this.currentVariant) {
      state = 'unavailable'
    } else if (!this.currentVariant.available) {
      state = 'sold-out'
    }

    this.submitTextEls.forEach(textEl => {
      textEl.classList.toggle('hide', textEl.dataset.state !== state)
    })

    this.button.disabled = state !== 'add'
  }

  initAdd () {
    if (!this.button) { return }

    this.button.addEventListener('click', async () => {
      if (!this.currentVariant || !this.currentVariant.available) { return }

      this.clearError()
      this.button.disabled = true
      this.button.classList.add('loading')

      const cartRes = await cartAPI.cartAdd({
        items: [{ id: this.currentVariant.id, quantity: 1 }]
      })

      this.button.classList.remove('loading')

      if (cartRes instanceof Error) {
        this.showError(cartRes.message)
        this.button.disabled = false
        return
      }

      this.button.disabled = false
      this.closeDrawer()
    })
  }

  showError (message) {
    if (!this.errorEl) { return }
    this.errorEl.innerText = message
    this.errorEl.classList.remove('hide')
  }

  clearError () {
    if (!this.errorEl) { return }
    this.errorEl.innerText = ''
    this.errorEl.classList.add('hide')
  }

  // The cart drawer opens itself off the cartAPI event, so close this one
  // to avoid stacking two drawers on top of each other.
  closeDrawer () {
    if (!this.drawer) { return }
    this.drawer.open = false
  }
}

/*
  Sticky drawer footer. Adds every product's currently selected variant to
  the cart in one request.
*/
const interrupterAddAll = class extends HTMLElement {
  constructor () {
    super()
    this.button = this.querySelector('[data-interrupter-add-all]')
    this.errorEl = this.querySelector('[data-interrupter-add-all-error]')
    this.drawer = this.closest('popup-element')
    this.initAddAll()
  }

  get products () {
    if (!this.drawer) { return [] }
    return [...this.drawer.querySelectorAll('interrupter-product')]
  }

  // Rows whose current selection has no variant, or a sold out one, are
  // skipped rather than failing the whole request.
  getItems () {
    return this.products.reduce((items, product) => {
      const variant = product.currentVariant
      if (variant && variant.available) {
        items.push({ id: variant.id, quantity: 1 })
      }
      return items
    }, [])
  }

  initAddAll () {
    if (!this.button) { return }

    this.button.addEventListener('click', async () => {
      const items = this.getItems()

      this.clearError()

      if (!items.length) {
        this.showError(this.button.dataset.emptyText)
        return
      }

      this.button.disabled = true
      this.button.classList.add('loading')

      const cartRes = await cartAPI.cartAdd({ items })

      this.button.classList.remove('loading')
      this.button.disabled = false

      if (cartRes instanceof Error) {
        this.showError(cartRes.message)
        return
      }

      this.closeDrawer()
    })
  }

  showError (message) {
    if (!this.errorEl) { return }
    this.errorEl.innerText = message
    this.errorEl.classList.remove('hide')
  }

  clearError () {
    if (!this.errorEl) { return }
    this.errorEl.innerText = ''
    this.errorEl.classList.add('hide')
  }

  closeDrawer () {
    if (!this.drawer) { return }
    this.drawer.open = false
  }
}

if (typeof window.customElements.get('interrupter-add-all') === 'undefined') {
  window.customElements.define('interrupter-add-all', interrupterAddAll)
}

if (typeof window.customElements.get('interrupter-product') === 'undefined') {
  window.customElements.define('interrupter-product', interrupterProduct)
}

/*
  Renders the interrupter markup in a readonly textarea so it can be copied
  and pasted into whichever app injects the tile into the collection grid.
*/
const interrupterCode = class extends HTMLElement {
  constructor () {
    super()
    this.textarea = this.querySelector('[data-interrupter-code]')
    this.button = this.querySelector('[data-interrupter-copy]')
    this.resetTimeout = null
    this.initCopy()
  }

  initCopy () {
    if (!this.textarea || !this.button) { return }

    this.button.addEventListener('click', async () => {
      const copied = await this.copy()

      this.button.innerText = copied
        ? this.button.dataset.copiedText
        : this.button.dataset.failedText

      if (this.resetTimeout !== null) { clearTimeout(this.resetTimeout) }
      this.resetTimeout = setTimeout(() => {
        this.button.innerText = this.button.dataset.copyText
      }, 2000)
    })
  }

  async copy () {
    // The clipboard API needs a secure context and is blocked in some
    // embedded previews, so fall back to selecting the field.
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(this.textarea.value)
        return true
      } catch (error) {
        console.error(error)
      }
    }

    return this.copyBySelection()
  }

  copyBySelection () {
    this.textarea.focus()
    this.textarea.select()

    try {
      return document.execCommand('copy')
    } catch (error) {
      console.error(error)
      return false
    }
  }
}

if (typeof window.customElements.get('interrupter-code') === 'undefined') {
  window.customElements.define('interrupter-code', interrupterCode)
}
